import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Group,
  Image,
  NumberInput,
  Pagination,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { showToast } from '../../lib/toast';
import { adminDelete, adminGet, adminPost } from './_api';
import { usePaginatedQuery } from './_hooks';

interface ModelMeta {
  id: string;
  label: string;
  description: string;
  output: 'base64' | 'binary';
  supportsDimensions: boolean;
  supportsNegative: boolean;
  defaultSteps: number;
  recommended?: boolean;
}

interface PresetMeta {
  key: string;
  label: string;
  description: string;
  promptTemplate: string;
  suggestedModel: string;
  negativePrompt?: string;
  width?: number;
  height?: number;
}

interface GalleryRow {
  id: string;
  model: string;
  prompt: string;
  source_prompt: string | null;
  preset_key: string | null;
  negative_prompt: string | null;
  width: number | null;
  height: number | null;
  url: string;
  created_at: string;
}

export const Route = createFileRoute('/admin/image-studio')({
  component: AdminImageStudioPage,
});

function AdminImageStudioPage() {
  const queryClient = useQueryClient();

  const { data: modelsData } = useQuery({
    queryKey: ['admin', 'ai-studio', 'models'],
    queryFn: () => adminGet<{ models: ModelMeta[] }>('/api/admin/ai-studio/models'),
  });
  const { data: presetsData } = useQuery({
    queryKey: ['admin', 'ai-studio', 'presets'],
    queryFn: () => adminGet<{ presets: PresetMeta[] }>('/api/admin/ai-studio/presets'),
  });

  const models = useMemo(() => modelsData?.models ?? [], [modelsData]);
  const presets = presetsData?.presets ?? [];

  const [presetKey, setPresetKey] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [sourcePrompt, setSourcePrompt] = useState<string | null>(null);
  const [negativePrompt, setNegativePrompt] = useState('');
  const [width, setWidth] = useState<number | ''>('');
  const [height, setHeight] = useState<number | ''>('');

  const [rewriting, setRewriting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<{ url: string; prompt: string } | null>(null);

  // Default the model select to the recommended one once models load.
  const selectedModel = model ?? models.find((m) => m.recommended)?.id ?? models[0]?.id ?? null;
  const modelMeta = models.find((m) => m.id === selectedModel) ?? null;

  function applyPreset(key: string): void {
    const preset = presets.find((p) => p.key === key);
    if (preset === undefined) return;
    setPresetKey(key);
    setPrompt(preset.promptTemplate);
    setSourcePrompt(null);
    setModel(preset.suggestedModel);
    setNegativePrompt(preset.negativePrompt ?? '');
    setWidth(preset.width ?? '');
    setHeight(preset.height ?? '');
  }

  async function handleRewrite(): Promise<void> {
    if (prompt.trim().length < 3) return;
    setRewriting(true);
    try {
      const resp = await adminPost<{ ok: boolean; rewritten: string }>('/api/admin/ai-studio/rewrite', {
        prompt,
        ...(presetKey !== null ? { preset_key: presetKey } : {}),
      });
      setSourcePrompt(prompt);
      setPrompt(resp.rewritten);
      showToast({ title: 'Prompt rewritten', body: 'Edit it further or generate.', kind: 'success' });
    } catch (err) {
      showToast({ title: 'Rewrite failed', body: messageOf(err), kind: 'error' });
    } finally {
      setRewriting(false);
    }
  }

  async function handleGenerate(): Promise<void> {
    if (selectedModel === null || prompt.trim().length < 3) return;
    setGenerating(true);
    setResult(null);
    try {
      const resp = await adminPost<{ ok: boolean; generation: { url: string; prompt: string } }>(
        '/api/admin/ai-studio/generate',
        {
          model: selectedModel,
          prompt,
          ...(modelMeta?.supportsNegative && negativePrompt.trim().length > 0 ? { negative_prompt: negativePrompt } : {}),
          ...(modelMeta?.supportsDimensions && width !== '' ? { width } : {}),
          ...(modelMeta?.supportsDimensions && height !== '' ? { height } : {}),
          ...(presetKey !== null ? { preset_key: presetKey } : {}),
          ...(sourcePrompt !== null ? { source_prompt: sourcePrompt } : {}),
        },
      );
      setResult(resp.generation);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'ai-studio', 'gallery'] });
      showToast({ title: 'Image generated', body: 'Saved to your gallery below.', kind: 'success' });
    } catch (err) {
      showToast({ title: 'Generation failed', body: messageOf(err), kind: 'error' });
    } finally {
      setGenerating(false);
    }
  }

  return (
    <AdminShell>
      <Stack gap="lg">
        <Box>
          <Title order={2}>Image studio</Title>
          <Text c="dimmed" size="sm">
            Generate platform imagery with Cloudflare Workers AI. Each generation is a metered AI call.
          </Text>
        </Box>

        <Grid gap="lg">
          {/* Controls */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card withBorder padding="lg" radius="md">
              <Stack gap="md">
                <Box>
                  <Text size="sm" fw={600} mb={6}>
                    Start from a preset
                  </Text>
                  <Group gap="xs">
                    {presets.map((p) => (
                      <Chip key={p.key} checked={presetKey === p.key} onClick={() => applyPreset(p.key)} variant="outline" color="tide">
                        {p.label}
                      </Chip>
                    ))}
                  </Group>
                  {presetKey !== null ? (
                    <Text size="xs" c="dimmed" mt={6}>
                      {presets.find((p) => p.key === presetKey)?.description}
                    </Text>
                  ) : null}
                </Box>

                <Textarea
                  label="Prompt"
                  placeholder="Describe the image, or pick a preset above to start."
                  value={prompt}
                  onChange={(e) => setPrompt(e.currentTarget.value)}
                  autosize
                  minRows={4}
                  maxRows={12}
                  maxLength={2000}
                />

                <Group>
                  <Button variant="light" color="tide" loading={rewriting} disabled={prompt.trim().length < 3} onClick={() => void handleRewrite()}>
                    Rewrite with AI
                  </Button>
                  {sourcePrompt !== null ? (
                    <Button variant="subtle" size="xs" color="gray" onClick={() => { setPrompt(sourcePrompt); setSourcePrompt(null); }}>
                      Revert to original
                    </Button>
                  ) : null}
                </Group>

                <Select
                  label="Model"
                  data={models.map((m) => ({ value: m.id, label: m.recommended ? `${m.label} (recommended)` : m.label }))}
                  value={selectedModel}
                  onChange={(v) => setModel(v)}
                  allowDeselect={false}
                />
                {modelMeta !== null ? (
                  <Text size="xs" c="dimmed" mt={-8}>
                    {modelMeta.description}
                  </Text>
                ) : null}

                {modelMeta?.supportsNegative ? (
                  <Textarea
                    label="Negative prompt (optional)"
                    placeholder="What to avoid"
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.currentTarget.value)}
                    autosize
                    minRows={2}
                  />
                ) : null}

                {modelMeta?.supportsDimensions ? (
                  <Group grow>
                    <NumberInput label="Width" placeholder="1024" min={256} max={2048} step={64} value={width} onChange={(v) => setWidth(typeof v === 'number' ? v : '')} />
                    <NumberInput label="Height" placeholder="1024" min={256} max={2048} step={64} value={height} onChange={(v) => setHeight(typeof v === 'number' ? v : '')} />
                  </Group>
                ) : null}

                <Button color="tide" loading={generating} disabled={selectedModel === null || prompt.trim().length < 3} onClick={() => void handleGenerate()}>
                  Generate image
                </Button>
              </Stack>
            </Card>
          </Grid.Col>

          {/* Result preview */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Card withBorder padding="lg" radius="md" h="100%">
              <Stack gap="sm">
                <Text size="sm" fw={600}>
                  Latest result
                </Text>
                {generating ? (
                  <Text c="dimmed" size="sm">
                    Generating — this can take a few seconds…
                  </Text>
                ) : result !== null ? (
                  <>
                    <Image src={result.url} radius="sm" alt={result.prompt} />
                    <Group>
                      <Anchor component="button" type="button" onClick={() => void downloadImage(result.url)}>
                        Download
                      </Anchor>
                    </Group>
                  </>
                ) : (
                  <Text c="dimmed" size="sm">
                    Your generated image will appear here, and is saved to the gallery below.
                  </Text>
                )}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        <Gallery queryClient={queryClient} />
      </Stack>
    </AdminShell>
  );
}

function Gallery({ queryClient }: { queryClient: ReturnType<typeof useQueryClient> }) {
  const { data, isLoading, page, setPage, totalPages } = usePaginatedQuery<GalleryRow>({
    queryKey: ['admin', 'ai-studio', 'gallery'],
    endpoint: '/api/admin/ai-studio',
    pageSize: 12,
  });

  async function remove(id: string): Promise<void> {
    try {
      await adminDelete(`/api/admin/ai-studio/${id}`);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'ai-studio', 'gallery'] });
    } catch {
      showToast({ title: 'Could not delete', body: 'Please try again.', kind: 'error' });
    }
  }

  const items = data?.items ?? [];

  return (
    <Stack gap="sm">
      <Title order={3}>Gallery</Title>
      {isLoading ? (
        <Text c="dimmed" size="sm">
          Loading…
        </Text>
      ) : items.length === 0 ? (
        <Text c="dimmed" size="sm">
          Nothing generated yet — your images will collect here.
        </Text>
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
            {items.map((g) => (
              <Card key={g.id} withBorder padding="xs" radius="md">
                <Card.Section>
                  <Image src={g.url} alt={g.prompt} h={160} fit="cover" />
                </Card.Section>
                <Stack gap={4} mt="xs">
                  <Badge variant="light" size="xs">
                    {shortModel(g.model)}
                  </Badge>
                  <Text size="xs" lineClamp={2}>
                    {g.prompt}
                  </Text>
                  <Group justify="space-between" mt={4}>
                    <Anchor component="button" type="button" size="xs" onClick={() => void downloadImage(g.url)}>
                      Download
                    </Anchor>
                    <Anchor component="button" type="button" size="xs" c="red" onClick={() => void remove(g.id)}>
                      Delete
                    </Anchor>
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
          {totalPages > 1 ? (
            <Group justify="flex-end">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          ) : null}
        </>
      )}
      <Alert variant="light" color="gray" title="Heads up">
        Generations are public assets stored in R2 and served from /img. Don't generate anything you wouldn't want
        publicly reachable by URL.
      </Alert>
    </Stack>
  );
}

function shortModel(id: string): string {
  const parts = id.split('/');
  return parts[parts.length - 1] ?? id;
}

/**
 * Force-download an image by fetching the bytes and saving via an object URL.
 * A plain `<a download>` to the /img path can be intercepted as a navigation
 * and hand back the SPA HTML; fetching the blob guarantees real image bytes and
 * surfaces a clear error rather than silently saving an HTML file.
 */
async function downloadImage(url: string): Promise<void> {
  try {
    const res = await fetch(url, { credentials: 'include' });
    const blob = await res.blob();
    if (!res.ok || !blob.type.startsWith('image/')) {
      throw new Error('not an image');
    }
    const objectUrl = URL.createObjectURL(blob);
    const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/jpeg' ? 'jpg' : blob.type.split('/')[1] ?? 'png';
    const base = url.split('/').pop()?.split('.')[0] ?? 'image';
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `wwaave-${base}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    showToast({ title: 'Could not download', body: 'Please try again in a moment.', kind: 'error' });
  }
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}
